/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { Modal, Grid } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { CloudUpload } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "utils/api";

function EditAttendanceModal({ open, onClose, attendance, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    topicCovered: "",
    type: "",
    homeWorkStatus: "",
    attendanceTime: "",
    homeWork: null,
    voiceNote: null,
  });

  useEffect(() => {
    if (attendance && open) {
      const attendanceTime = attendance.attendanceTime
        ? (() => {
            const date = new Date(attendance.attendanceTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          })()
        : "";

      setFormData({
        topicCovered: attendance.topicCovered || "",
        type: attendance.type || "",
        homeWorkStatus: attendance.homeWorkStatus || "",
        attendanceTime: attendanceTime,
        homeWork: null,
        voiceNote: null,
      });
    }
  }, [attendance, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    // Extract the actual value if it's an event object
    const actualValue = value?.target ? value.target.value : value;

    setFormData((prev) => ({
      ...prev,
      [name]: actualValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.topicCovered?.trim()) {
      toast.error("Topic covered is required");
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("attendanceId", attendance.id || attendance._id);
      payload.append("topicCovered", formData.topicCovered.trim());

      // Only add type if it's a valid string value
      if (formData.type && typeof formData.type === "string") {
        payload.append("type", formData.type);
      }

      // Only add homeWorkStatus if it's a valid string value
      if (formData.homeWorkStatus && typeof formData.homeWorkStatus === "string") {
        payload.append("homeWorkStatus", formData.homeWorkStatus);
      }

      if (formData.attendanceTime) {
        payload.append("attendanceTime", new Date(formData.attendanceTime).toISOString());
      }

      // Add files if selected
      if (formData.homeWork) {
        payload.append("homeWork", formData.homeWork);
      }

      if (formData.voiceNote) {
        payload.append("voiceNote", formData.voiceNote);
      }

      const response = await api.put(`/attendance/${attendance.id || attendance._id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success("Attendance updated successfully");
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error(error.response?.data?.message || "Failed to update attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      topicCovered: "",
      type: "",
      homeWorkStatus: "",
      attendanceTime: "",
      homeWork: null,
      voiceNote: null,
    });
    onClose();
  };

  const typeOptions = [
    { label: "Regular", value: "REGULAR" },
    { label: "Extra", value: "EXTRA" },
    { label: "Recovery", value: "RECOVERY" },
  ];

  const homeWorkStatusOptions = [
    { label: "Done", value: "DONE" },
    { label: "Incomplete", value: "INCOMPLETE" },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <ArgonBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "600px" },
          background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
          borderRadius: "20px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
      >
        <ArgonBox
          sx={{
            background: "white",
            borderRadius: "20px",
            m: 2,
            p: 3,
          }}
        >
          <ArgonTypography variant="h5" fontWeight="bold" mb={3}>
            Edit Attendance
          </ArgonTypography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Topic Covered *
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="text"
                  name="topicCovered"
                  value={formData.topicCovered}
                  onChange={handleInputChange}
                  placeholder="Enter topic covered"
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Type
                  </ArgonTypography>
                </ArgonBox>
                <Dropdown
                  options={typeOptions}
                  value={formData.type}
                  onChange={(value) => handleDropdownChange("type", value)}
                  placeholder="Select type"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Homework Status
                  </ArgonTypography>
                </ArgonBox>
                <Dropdown
                  options={homeWorkStatusOptions}
                  value={formData.homeWorkStatus}
                  onChange={(value) => handleDropdownChange("homeWorkStatus", value)}
                  placeholder="Select status"
                />
              </Grid>

              <Grid item xs={12}>
                <ArgonBox mb={1}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    Attendance Date & Time
                  </ArgonTypography>
                </ArgonBox>
                <ArgonInput
                  type="datetime-local"
                  name="attendanceTime"
                  value={formData.attendanceTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              {/* File Uploads */}
              <Grid item xs={12} sm={6}>
                <ArgonBox>
                  <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                    Homework Assignment
                  </ArgonTypography>

                  {/* Show existing file if available */}
                  {attendance?.homeWork && (
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                        Current File:
                      </ArgonTypography>
                      <ArgonBox
                        sx={{
                          border: "1px solid #e9ecef",
                          borderRadius: 2,
                          p: 1.5,
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          📄 Uploaded File
                        </ArgonTypography>
                        <ArgonButton
                          component="a"
                          href={attendance.homeWork}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ fontSize: "11px", py: 0.5, px: 1 }}
                        >
                          View
                        </ArgonButton>
                      </ArgonBox>
                    </ArgonBox>
                  )}

                  {/* Upload section */}
                  <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                    {attendance?.homeWork ? "Upload New Homework" : "Upload Homework"}
                  </ArgonTypography>
                  <ArgonBox
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d2d6da",
                      borderRadius: 2,
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      minHeight: "100px",
                      background: formData.homeWork ? "#f0f9ff" : "transparent",
                      "&:hover": {
                        borderColor: "#5e72e4",
                        background: "#f8f9fe",
                      },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                    <ArgonTypography variant="caption" color="text" textAlign="center">
                      {formData.homeWork
                        ? `✓ ${formData.homeWork.name}`
                        : "Drop files here or browse"}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="secondary" fontSize="10px" mt={0.5}>
                      Max size: 50MB
                    </ArgonTypography>
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                    />
                  </ArgonBox>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonBox>
                  <ArgonTypography variant="caption" fontWeight="bold" mb={1} display="block">
                    Voice Note
                  </ArgonTypography>

                  {/* Show existing file if available */}
                  {attendance?.voiceNote && (
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                        Current File:
                      </ArgonTypography>
                      <ArgonBox
                        sx={{
                          border: "1px solid #e9ecef",
                          borderRadius: 2,
                          p: 1.5,
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          🎵 Audio File
                        </ArgonTypography>
                        <ArgonButton
                          component="a"
                          href={attendance.voiceNote}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ fontSize: "11px", py: 0.5, px: 1 }}
                        >
                          Play
                        </ArgonButton>
                      </ArgonBox>
                    </ArgonBox>
                  )}

                  {/* Upload section */}
                  <ArgonTypography variant="caption" color="text" mb={0.5} display="block">
                    {attendance?.voiceNote ? "Upload New Voice Note" : "Upload Voice Note"}
                  </ArgonTypography>
                  <ArgonBox
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d2d6da",
                      borderRadius: 2,
                      p: 2.5,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      minHeight: "100px",
                      background: formData.voiceNote ? "#f0f9ff" : "transparent",
                      "&:hover": {
                        borderColor: "#5e72e4",
                        background: "#f8f9fe",
                      },
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                    <ArgonTypography variant="caption" color="text" textAlign="center">
                      {formData.voiceNote
                        ? `✓ ${formData.voiceNote.name}`
                        : "Drop files here or browse"}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="secondary" fontSize="10px" mt={0.5}>
                      Max size: 50MB
                    </ArgonTypography>
                    <input
                      type="file"
                      hidden
                      accept="audio/*"
                      onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                    />
                  </ArgonBox>
                </ArgonBox>
              </Grid>
            </Grid>

            <ArgonBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
                Cancel
              </ArgonButton>
              <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                {loading ? "Updating..." : "Update Attendance"}
              </ArgonButton>
            </ArgonBox>
          </form>
        </ArgonBox>
      </ArgonBox>
    </Modal>
  );
}

export default EditAttendanceModal;
